/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-08-07 01:58:32
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import { getJSON } from 'redux-api-middleware';
import { I18n } from '@shopify/react-i18n';
import { processRes } from 'src/helpers/actions-helper';

import apiService from 'src/services/api-service';
import {
  LIST_FILTER_TREE_REQUEST,
  LIST_FILTER_TREE_SUCCESS,
  LIST_FILTER_TREE_FAILURE,
  SEARCH_FILTER_TREE_REQUEST,
  SEARCH_FILTER_TREE_SUCCESS,
  SEARCH_FILTER_TREE_FAILURE,
  CHANGE_FILTER_TREE_STATUS_REQUEST,
  CHANGE_FILTER_TREE_STATUS_SUCCESS,
  CHANGE_FILTER_TREE_STATUS_FAILURE,
  CLONE_FILTER_TREE_REQUEST,
  CLONE_FILTER_TREE_SUCCESS,
  CLONE_FILTER_TREE_FAILURE,
  REMOVE_FILTER_TREE_REQUEST,
  REMOVE_FILTER_TREE_SUCCESS,
  REMOVE_FILTER_TREE_FAILURE
} from 'stores/flow-type/filter-tree/management.type';
import { mixpanelV3Helper } from 'src/helpers/mixpanel-v3';
import { trackFSEvent } from 'src/integrations/fullStory';

/**
 * call to admin list filter tree
 * @return {object} The RSAA object for API middleware
 */
export const listFilterTree = (q, skip, limit, i18n: I18n) => {
  return apiService.sendRequest(
    'admin-v3.filter-tree.list-filter-tree',
    { queries: { q, skip, limit } },
    [
      LIST_FILTER_TREE_REQUEST,
      {
        type: LIST_FILTER_TREE_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);
          return { data, q, skip, limit };
        }
      },
      {
        type: LIST_FILTER_TREE_FAILURE,
        payload: async (action, state, res) =>
          await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.FilterTree.Actions.listFilterTree'),
            i18n
          )
      }
    ]
  );
};

/**
 * call to admin search filter tree
 * @return {object} The RSAA object for API middleware
 */
export const searchFilterTree = (q, skip, limit, i18n: I18n) => {
  return apiService.sendRequest(
    'admin-v3.filter-tree.list-filter-tree',
    { queries: { q, skip, limit } },
    [
      SEARCH_FILTER_TREE_REQUEST,
      {
        type: SEARCH_FILTER_TREE_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);
          return { data };
        }
      },
      {
        type: SEARCH_FILTER_TREE_FAILURE,
        payload: async (action, state, res) =>
          await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.FilterTree.Actions.searchFilterTree'),
            i18n
          )
      }
    ]
  );
};

/**
 * call to admin update filter tree
 * @return {object} The RSAA object for API middleware
 */
export const changeFilterTreeStatus = (filterId, status, i18n: I18n) => {
  const body = { status };
  return apiService.sendRequest(
    'admin-v3.filter-tree.update-filter-tree',
    { params: { filterId }, body },
    [
      CHANGE_FILTER_TREE_STATUS_REQUEST,
      {
        type: CHANGE_FILTER_TREE_STATUS_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          if (status === 'active') {
            mixpanelV3Helper.trackEventData('Turn On Filter Tree', {
              feature: 'Manage Filter Tree',
              optionTypes: data?.updatedFilterTree?.filterOptions?.map(e => e.filterType),
              noFilterOptions: data?.updatedFilterTree?.filterOptions?.length,
              collections: data?.updatedFilterTree?.collections?.map(e => e.title)
            });

            // Track Full Story event
            trackFSEvent('Turn On Filter Tree', {
              feature: 'Manage Filter Tree',
              optionTypes: data?.updatedFilterTree?.filterOptions?.map(e => e.filterType),
              noFilterOptions: data?.updatedFilterTree?.filterOptions?.length,
              collections: data?.updatedFilterTree?.collections?.map(e => e.title)
            });
          }
          if (status === 'disabled') {
            mixpanelV3Helper.trackEventData('Turn Off Filter Tree', {
              feature: 'Manage Filter Tree',
              optionTypes: data?.updatedFilterTree?.filterOptions?.map(e => e.filterType),
              noFilterOptions: data?.updatedFilterTree?.filterOptions?.length,
              collections: data?.updatedFilterTree?.collections?.map(e => e.title)
            });

            // Track Full Story event
            trackFSEvent('Turn Off Filter Tree', {
              feature: 'Manage Filter Tree',
              optionTypes: data?.updatedFilterTree?.filterOptions?.map(e => e.filterType),
              noFilterOptions: data?.updatedFilterTree?.filterOptions?.length,
              collections: data?.updatedFilterTree?.collections?.map(e => e.title)
            });
          }

          return { data, status, i18n };
        }
      },
      {
        type: CHANGE_FILTER_TREE_STATUS_FAILURE,
        payload: async (action, state, res) => {
          const data = await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.FilterTree.Actions.changeFilterTreeStatus'),
            i18n
          );

          return { ...data, i18n };
        }
      }
    ]
  );
};

/**
 * call to admin clone filter tree
 * @return {object} The RSAA object for API middleware
 */
export const cloneFilterTree = (filterId, cloneTimes, filterTree, i18n: I18n) => {
  const body = { cloneTimes };
  return apiService.sendRequest(
    'admin-v3.filter-tree.clone-filter-tree',
    { params: { filterId }, body },
    [
      CLONE_FILTER_TREE_REQUEST,
      {
        type: CLONE_FILTER_TREE_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          // Track Full Story event
          trackFSEvent('Duplicate Filter Tree', {
            feature: 'Manage Filter Tree',
            optionTypes: filterTree?.filterOptions?.map(e => e.filterType),
            noFilterOptions: filterTree?.filterOptions?.length,
            collections: filterTree?.collections?.map(e => e.title),
            noDuplicate: cloneTimes
          });

          return { data };
        }
      },
      {
        type: CLONE_FILTER_TREE_FAILURE,
        payload: async (action, state, res) => {
          const errorResult = await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.FilterTree.Actions.cloneFilterTree'),
            i18n
          );

          return errorResult;
        }
      }
    ]
  );
};

/**
 * call to admin remoe filter tree
 * @return {object} The RSAA object for API middleware
 */
export const removeFilterTree = (filterId, i18n: I18n) => {
  return apiService.sendRequest(
    'admin-v3.filter-tree.remove-filter-tree',
    { params: { filterId } },
    [
      REMOVE_FILTER_TREE_REQUEST,
      {
        type: REMOVE_FILTER_TREE_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          // Track Full Story event
          const filterTrees = _.get(state, 'managementFilterTree.customFilterTrees', '');
          const filterTree = _.find(filterTrees, filterTree => filterTree.filterId === filterId);
          trackFSEvent('Delete Filter Tree', {
            feature: 'Manage Filter Tree',
            optionTypes: filterTree?.filterOptions?.map(e => e.filterType),
            noFilterOptions: filterTree?.filterOptions?.length,
            collections: filterTree?.collections?.map(e => e.title)
          });

          return { data, filterId, i18n };
        }
      },
      {
        type: REMOVE_FILTER_TREE_FAILURE,
        payload: async (action, state, res) => {
          const data = await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.FilterTree.Actions.removeFilterTree'),
            i18n
          );

          return { ...data, i18n };
        }
      }
    ]
  );
};
